import React, { useCallback } from 'react';

interface MaskedInputProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Texto curto sob o campo: como preencher, ou um exemplo. */
  ajuda?: string;
}

// ==================== KM (quilometragem) ====================
// Exibe: 123.456 km | Armazena: 123456
// Interpreta o valor numericamente em vez de descartar não-dígitos: a API devolve
// decimal como string ("85000.00"), e o strip anterior transformava isso em
// "8500000" — o campo exibia 8.500.000 km e re-salvava esse valor na edição.
export const formatKm = (value: string | number): string => {
  const num = Math.round(typeof value === 'string' ? parseFloat(value) : Number(value));
  if (!Number.isFinite(num)) return '';
  return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' km';
};

export const KmInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const displayValue = value ? formatKm(value) : '';

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="numeric" id={name} name={name} value={displayValue} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || '0 km'}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};

// ==================== LITROS ====================
// Exibe: 45,5 L | Armazena: 45.5
// minimumFractionDigits era 1, o que injetava uma vírgula que o usuário não digitou.
// Como o handleChange trata vírgula como separador decimal significativo, o campo
// realimentava o próprio formatador: digitar "45" gravava 4,05.
export const formatLitros = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || !value) return '';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 }) + ' L';
};

export const LitrosInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className, ajuda }) => {
  // Enquanto o campo está focado exibimos exatamente o que foi digitado, sem sufixo
  // nem separador injetado. Formatar só fora do foco elimina a realimentação e
  // devolve o comportamento normal de backspace.
  const [focado, setFocado] = React.useState(false);
  const [rascunho, setRascunho] = React.useState('');

  const displayValue = focado ? rascunho : (value ? formatLitros(value) : '');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Ponto e virgula valem como separador decimal. O teclado numerico do
    // celular mostra um ou outro conforme a regiao configurada no aparelho, e
    // o ponto era descartado: "112.67" litros virava 11267.
    const digitado = e.target.value.replace(/\./g, ',').replace(/[^\d,]/g, '');
    const partes = digitado.split(',');
    const temDecimal = partes.length > 1;
    const inteira = partes[0];
    const decimais = partes.slice(1).join('').slice(0, 3);
    const numericValue = temDecimal ? inteira + '.' + decimais : inteira;

    // O rascunho mostra o valor JA normalizado, e nao o que foi digitado: sem
    // isto, teclar uma segunda virgula deixava "9,7,3" na tela e salvava 9,73.
    // O usuario via um numero e o sistema guardava outro, sem nenhum aviso.
    setRascunho(temDecimal ? inteira + ',' + decimais : inteira);
    const synth = { ...e, target: { ...e.target, name, value: numericValue } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  const handleFocus = useCallback(() => {
    setRascunho(value ? String(value).replace('.', ',') : '');
    setFocado(true);
  }, [value]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="decimal" id={name} name={name} value={displayValue} onChange={handleChange}
        onFocus={handleFocus} onBlur={() => setFocado(false)}
        disabled={disabled} placeholder={placeholder || 'Ex.: 112,676'}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
      {ajuda && <p className="mt-1 text-xs text-gray-500">{ajuda}</p>}
    </div>
  );
};

// ==================== DECIMAL genérico ====================
// Para grandezas decimais que não são dinheiro nem litros (consumo km/L, etc).
// Exibe: 12,5 km/L | Armazena: 12.5
interface DecimalInputProps extends MaskedInputProps {
  sufixo?: string;
  casas?: number;
}

export const DecimalInput: React.FC<DecimalInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className, sufixo = '', casas = 2, ajuda }) => {
  const [focado, setFocado] = React.useState(false);
  const [rascunho, setRascunho] = React.useState('');

  const formatar = (v: string | number): string => {
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (isNaN(num)) return '';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas }) + sufixo;
  };

  const displayValue = focado ? rascunho : (value ? formatar(value) : '');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Ponto e virgula valem como separador decimal. O teclado numerico do
    // celular mostra um ou outro conforme a regiao configurada no aparelho, e
    // o ponto era descartado: "112.67" litros virava 11267.
    const digitado = e.target.value.replace(/\./g, ',').replace(/[^\d,]/g, '');
    const partes = digitado.split(',');
    const temDecimal = partes.length > 1;
    const inteira = partes[0];
    const decimais = partes.slice(1).join('').slice(0, casas);
    const numericValue = temDecimal ? inteira + '.' + decimais : inteira;

    // Ver a nota do LitrosInput: o rascunho acompanha o valor normalizado para
    // o que esta na tela ser o que vai ser salvo.
    setRascunho(temDecimal ? inteira + ',' + decimais : inteira);
    const synth = { ...e, target: { ...e.target, name, value: numericValue } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange, casas]);

  const handleFocus = useCallback(() => {
    setRascunho(value ? String(value).replace('.', ',') : '');
    setFocado(true);
  }, [value]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="decimal" id={name} name={name} value={displayValue} onChange={handleChange}
        onFocus={handleFocus} onBlur={() => setFocado(false)}
        disabled={disabled} placeholder={placeholder || `0${sufixo}`}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
      {ajuda && <p className="mt-1 text-xs text-gray-500">{ajuda}</p>}
    </div>
  );
};

// ==================== CPF ====================
// Exibe: 123.456.789-01 | Armazena: 12345678901
export const CpfInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatCpf = (v: string): string => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return d.slice(0, 3) + '.' + d.slice(3);
    if (d.length <= 9) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6);
    return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="numeric" id={name} name={name} value={formatCpf(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || '000.000.000-00'} maxLength={14}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};

// ==================== CNPJ ====================
// Exibe: 12.345.678/0001-99 | Armazena: 12345678000199
export const CnpjInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatCnpj = (v: string): string => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return d.slice(0, 2) + '.' + d.slice(2);
    if (d.length <= 8) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5);
    if (d.length <= 12) return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8);
    return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="numeric" id={name} name={name} value={formatCnpj(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || '00.000.000/0000-00'} maxLength={18}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};

// ==================== TELEFONE / CELULAR ====================
// Celular: (11) 99999-8888 | Fixo: (11) 3456-7890 | Armazena: somente digitos
export const PhoneInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatPhone = (v: string): string => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d.length ? '(' + d : '';
    if (d.length <= 6) return '(' + d.slice(0, 2) + ') ' + d.slice(2);
    if (d.length <= 10) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 6) + '-' + d.slice(6);
    return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="tel" id={name} name={name} value={formatPhone(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || '(00) 00000-0000'} maxLength={15}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};

// ==================== CEP ====================
// Exibe: 01001-000 | Armazena: 01001000
export const CepInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatCep = (v: string): string => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    if (d.length <= 5) return d;
    return d.slice(0, 5) + '-' + d.slice(5);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="numeric" id={name} name={name} value={formatCep(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || '00000-000'} maxLength={9}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};

// ==================== PLACA ====================
// Exibe: ABC1D23 (Mercosul) ou ABC-1234 (antiga) | Armazena: sem hifen
export const PlacaInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatPlaca = (v: string): string => {
    const clean = v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 7);
    if (clean.length <= 3) return clean;
    // Formato antigo ABC-1234
    if (clean.length === 7 && /^[A-Z]{3}\d{4}$/.test(clean)) {
      return clean.slice(0, 3) + '-' + clean.slice(3);
    }
    return clean;
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const clean = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 7);
    const synth = { ...e, target: { ...e.target, name, value: clean } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" id={name} name={name} value={formatPlaca(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || 'ABC1D23'} maxLength={8}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase'} />
    </div>
  );
};

// ==================== DOCUMENTO (CPF ou CNPJ) ====================
// Para campos que aceitam pessoa física ou jurídica, como documento de fornecedor.
// Formata conforme a quantidade de dígitos: até 11 usa CPF, acima disso CNPJ.
// Exibe: 123.456.789-01 ou 12.345.678/0001-99 | Armazena: somente dígitos
export const DocumentoInput: React.FC<MaskedInputProps> = ({ name, value, onChange, label, required, disabled, placeholder, className }) => {
  const formatar = (v: string): string => {
    const d = (v || '').replace(/\D/g, '').slice(0, 14);
    if (d.length <= 11) {
      if (d.length <= 3) return d;
      if (d.length <= 6) return d.slice(0, 3) + '.' + d.slice(3);
      if (d.length <= 9) return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6);
      return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9);
    }
    return d.slice(0, 2) + '.' + d.slice(2, 5) + '.' + d.slice(5, 8) + '/' + d.slice(8, 12) + '-' + d.slice(12);
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
    const synth = { ...e, target: { ...e.target, name, value: digits } } as React.ChangeEvent<HTMLInputElement>;
    onChange(synth);
  }, [name, onChange]);

  return (
    <div>
      {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <input type="text" inputMode="numeric" id={name} name={name} value={formatar(value || '')} onChange={handleChange}
        disabled={disabled} placeholder={placeholder || 'CPF ou CNPJ'} maxLength={18}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'} />
    </div>
  );
};
